// Browser error reporter for the demo build.
//
// Captures unhandled errors, unhandled promise rejections, and console.error
// output, and POSTs them to /report/error on the same origin. The demo Node
// server logs those payloads to stderr so they surface in `docker logs`.
//
// Designed to be zero-dependency and safe to ship to the browser: it rate-
// limits, deduplicates recent payloads, and falls back to sendBeacon on
// page unload.

const ENDPOINT = process.env.REACT_APP_ERROR_REPORT_URL || '/report/error';
const MAX_REPORTS_PER_WINDOW = 30;
const WINDOW_MS = 10_000;
const DEDUPE_WINDOW_MS = 2_000;
const MAX_PAYLOAD_CHARS = 8_000;

type Source =
  | 'window.error'
  | 'unhandledrejection'
  | 'console.error'
  | 'errorBoundary'
  | 'manual';

interface ReportPayload {
  source: Source;
  message: string;
  stack?: string;
  name?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  componentStack?: string;
  href: string;
  at: string;
  extra?: unknown;
}

let installed = false;
let reportsInWindow = 0;
let windowStartedAt = 0;
const recent = new Map<string, number>();
let reporting = false;

function stringifySafe(value: unknown): string {
  if (value == null) return String(value);
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.stack || `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value, Object.getOwnPropertyNames(value as object));
  } catch {
    try { return String(value); } catch { return '[unstringifiable]'; }
  }
}

function shouldSend(signature: string): boolean {
  const now = Date.now();
  if (now - windowStartedAt > WINDOW_MS) {
    windowStartedAt = now;
    reportsInWindow = 0;
  }
  if (reportsInWindow >= MAX_REPORTS_PER_WINDOW) return false;
  const last = recent.get(signature);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recent.set(signature, now);
  if (recent.size > 200) {
    for (const [k, t] of recent) {
      if (now - t > WINDOW_MS) recent.delete(k);
    }
  }
  reportsInWindow += 1;
  return true;
}

function send(payload: ReportPayload) {
  const signature = `${payload.source}|${payload.message}|${payload.filename || ''}:${payload.lineno || ''}`;
  if (!shouldSend(signature)) return;
  const body = JSON.stringify(payload).slice(0, MAX_PAYLOAD_CHARS);
  // Use fetch with keepalive (not sendBeacon). sendBeacon always sends with
  // credentials=include which is incompatible with a wildcard CORS origin,
  // and Firefox rejects it. keepalive fetch with credentials:'omit' works
  // fine against Access-Control-Allow-Origin: *.
  reporting = true;
  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => { /* swallow — nowhere to report a report failure */ });
  } finally {
    reporting = false;
  }
}

export function reportError(partial: Partial<ReportPayload> & { source: Source; message: string }) {
  send({
    href: typeof window !== 'undefined' ? window.location.href : '',
    at: new Date().toISOString(),
    ...partial,
  });
}

export function reportBoundaryError(error: Error, info: { componentStack: string }) {
  reportError({
    source: 'errorBoundary',
    message: error?.message || String(error),
    name: error?.name,
    stack: error?.stack,
    componentStack: info?.componentStack,
  });
}

export function installErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    const err = event.error;
    reportError({
      source: 'window.error',
      message: event.message || (err && err.message) || 'Unknown error',
      name: err && err.name,
      stack: err && err.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason: any = event.reason;
    reportError({
      source: 'unhandledrejection',
      message: (reason && (reason.message || reason.toString && reason.toString())) || 'Unhandled promise rejection',
      name: reason && reason.name,
      stack: reason && reason.stack,
      extra: reason && typeof reason === 'object' ? undefined : reason,
    });
  });

  const origConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    origConsoleError(...args);
    if (reporting) return;
    try {
      const first = args[0];
      const message = args.map(stringifySafe).join(' ');
      const stack = first instanceof Error ? first.stack : undefined;
      const name = first instanceof Error ? first.name : undefined;
      reportError({ source: 'console.error', message, name, stack });
    } catch {
      // never let the reporter break the host console.error
    }
  };
}
